import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Container,
  Paper,
  Snackbar,
  Alert as MuiAlert,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { motion } from 'framer-motion';
import { useAuth } from './auth/AuthContext';
import api from '../utils/api';

const PublishScholarship = () => {
  const navigate = useNavigate();
  const { isAdminAuthenticated } = useAuth();
  const [formData, setFormData] = useState({
    title: '',
    type: 'Scholarship',
    deadline: null,
    hostOrganization: '',
    level: '',
    eligibleCountries: '',
    fieldsOfStudy: '',
    benefits: '',
    duration: '',
    eligibilityCriteria: '',
    applicationProcess: '',
    officialLink: '',
    contactInformation: '',
    selectionProcess: '',
    additionalNotes: '',
  });
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.deadline) newErrors.deadline = 'Deadline is required';
    if (!formData.hostOrganization.trim()) newErrors.hostOrganization = 'Host organization is required';
    if (!formData.eligibilityCriteria.trim()) newErrors.eligibilityCriteria = 'Eligibility criteria are required';
    if (!formData.applicationProcess.trim()) newErrors.applicationProcess = 'Application process is required';
    if (!formData.officialLink.trim()) newErrors.officialLink = 'Official link is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!isAdminAuthenticated) {
      setSnackbar({
        open: true,
        message: 'Please login as admin first',
        severity: 'error',
      });
      return;
    }

    if (!validateForm()) {
      setSnackbar({
        open: true,
        message: 'Please fill in all required fields',
        severity: 'error',
      });
      return;
    }

    try {
      await api.post('/api/schools/publish-scholarship', {
        ...formData,
        deadline: formData.deadline ? formData.deadline.toISOString() : null,
      });
      setSnackbar({
        open: true,
        message: 'Scholarship published successfully!',
        severity: 'success',
      });
      setTimeout(() => navigate(-1), 2000);
    } catch (error) {
      console.error('Error publishing scholarship:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to publish scholarship. Please try again.',
        severity: 'error',
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatLabel = (key) =>
    key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase());

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="sm" sx={{ py: 4, bgcolor: '#f8f9fa', minHeight: '100vh' }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2c3e50', mb: 4, textAlign: 'center' }}>
            Publish Scholarship/Opportunity
          </Typography>
          <Paper sx={{ p: 3, bgcolor: '#fff', borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
            {Object.keys(formData).map((key, index) => (
              <motion.div
                key={key}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
              >
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body1" sx={{ color: '#2c3e50', fontWeight: 500, mb: 1 }}>
                    {formatLabel(key)}
                  </Typography>
                  {key === 'type' ? (
                    <FormControl fullWidth error={!!errors.type} sx={{ bgcolor: '#fff' }}>
                      <InputLabel>{formatLabel(key)}</InputLabel>
                      <Select
                        value={formData.type}
                        onChange={(e) => handleChange('type', e.target.value)}
                        label={formatLabel(key)}
                      >
                        {[
                          'Scholarship',
                          'Fellowship',
                          'Internship',
                          'Grant',
                          'Competition',
                          'Exchange Program',
                        ].map((type) => (
                          <MenuItem key={type} value={type}>
                            {type}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.type && <Typography color="error" variant="caption">{errors.type}</Typography>}
                    </FormControl>
                  ) : key === 'deadline' ? (
                    <DatePicker
                      value={formData.deadline}
                      onChange={(newValue) => handleChange('deadline', newValue)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          error={!!errors.deadline}
                          helperText={errors.deadline}
                          sx={{ bgcolor: '#fff' }}
                        />
                      )}
                    />
                  ) : (
                    <TextField
                      fullWidth
                      multiline={key === 'eligibilityCriteria' || key === 'applicationProcess'}
                      rows={key === 'eligibilityCriteria' || key === 'applicationProcess' ? 4 : 1}
                      value={formData[key]}
                      onChange={(e) => handleChange(key, e.target.value)}
                      placeholder={`Enter ${formatLabel(key).toLowerCase()}`}
                      error={!!errors[key]}
                      helperText={errors[key]}
                      sx={{ bgcolor: '#fff' }}
                    />
                  )}
                </Box>
              </motion.div>
            ))}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: Object.keys(formData).length * 0.05 }}
            >
              <Button
                variant="contained"
                onClick={handleSubmit}
                fullWidth
                sx={{
                  bgcolor: '#6200ee',
                  '&:hover': { bgcolor: '#7f39fb' },
                  borderRadius: 2,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  mt: 2,
                }}
              >
                Publish Scholarship
              </Button>
            </motion.div>
          </Paper>
        </motion.div>
        <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
          <MuiAlert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </MuiAlert>
        </Snackbar>
      </Container>
    </LocalizationProvider>
  );
};

export default PublishScholarship;